import { useState } from "react";
import { useAuth } from "@/auth/session";
import TailoredResumeDialog from "@/components/envoy/history/TailoredResumeDialog";
import TailorAsk from "@/components/posting/TailorAsk";
import { BookmarkIcon, ResumeIcon, SparkIcon } from "@/components/icons";
import { actionProps } from "@/lib/controlAnalytics";
import { isNotQueueable, useQueueActions } from "@/lib/hooks/envoy";
import { useAddSkills } from "@/lib/hooks/resume";
import type { TailorChoices } from "@/lib/posting/tailorChoices";
import { useSaveActions, useSavedIds } from "@/lib/hooks/saved";
import { useStanding } from "@/lib/hooks/standingContext";
import { useGate } from "./gateContext";
import { GRAD_BUTTON, OUTLINE_BUTTON } from "./styles";

/**
 * Save, on every card and every posting, for everyone and at full
 * contrast. Signed out it opens Save this job; signed in it keeps the
 * posting, or lets it go, at once.
 */
export function SaveButton({
    postingId,
    title,
    size = "md",
}: {
    postingId: string;
    title: string;
    size?: "sm" | "md" | "page" | "tall";
}) {
    const { state } = useAuth();
    const member = state.status === "signed-in";
    const { ask } = useGate();
    const saved = useSavedIds(member);
    const { save, forget } = useSaveActions();
    const kept = member && saved.has(postingId);
    const height = {
        sm: "min-h-[34px] max-[760px]:min-h-11 text-[12.5px] rounded-[10px] px-3",
        md: "min-h-[38px] max-[760px]:min-h-11 text-[13px] rounded-[10px] px-3",
        page: "min-h-[38px] max-[760px]:min-h-11 text-ui rounded-[10px] px-3",
        // Beside a filled or outlined Apply, so on the quieter edge.
        tall: `min-h-[46px] text-[15px] leading-[22px] rounded-control px-4 ${kept ? "" : "!border-line-strong"}`,
    }[size];
    return (
        <button
            {...actionProps(kept ? "posting_unsave_clicked" : "posting_save_clicked", {
                source: "SaveButton",
                posting_id: postingId,
                mode: member ? "member" : "visitor",
            })}
            type="button"
            aria-pressed={member ? kept : undefined}
            aria-label={`${kept ? "Saved" : "Save"}: ${title}`}
            onClick={(event) => {
                event.stopPropagation();
                if (!member) ask("save", postingId);
                else if (kept) forget.mutate({ postingId });
                else save.mutate({ postingId, title });
            }}
            className={`${OUTLINE_BUTTON} ${height} gap-1.5 ${
                kept ? "!border-brand-ink !bg-tint-green" : ""
            }`}
        >
            <BookmarkIcon
                filled={kept}
                className={`size-[15px] transition-transform duration-300 ease-[cubic-bezier(.3,1.6,.5,1)] ${kept ? "scale-110" : ""}`}
            />
            {kept ? "Saved" : "Save"}
        </button>
    );
}

/**
 * Tailor my resume: for a visitor, the sheet that asks for an account
 * and a resume; for an account with tailors left, the dialog that asks what
 * to emphasize, then the posting goes to the envoy's queue and a resume is
 * written for it; with none left, the upgrade sheet. A failed tailor is not
 * asked: the queue answers it as it stands, so what was typed would be lost.
 * Tried again, it fades and says Tailoring… until the next attempt ends.
 */
export function TailorButton({
    postingId,
    job,
    failed = false,
    retrying = false,
    queued = false,
    tailored = false,
    resumeId,
    size = "md",
    outline = false,
    onTailor,
}: {
    postingId: string;
    /** What the dialog names the posting by. */
    job: { title: string; company?: string | null };
    /** Its last tailor failed and wrote no resume: the button says Try again. */
    failed?: boolean;
    /** A failed tailor's next attempt is running: faded, Tailoring…, and does nothing. */
    retrying?: boolean;
    /** Already in the queue: the button says so and does nothing. */
    queued?: boolean;
    /** Already tailored, its resume ready to send: the button says so and does nothing. */
    tailored?: boolean;
    /** The tailored resume: once tailored, the button opens it as See resume. */
    resumeId?: string | null;
    size?: "sm" | "md" | "page" | "tall" | "wide";
    /** Not the next step on this screen, so it leaves the gradient to the one that is. */
    outline?: boolean;
    /** A page with its own way to a tailor. Only someone with none left still gets the sheet. */
    onTailor?: () => void;
}) {
    const [viewing, setViewing] = useState(false);
    const [asking, setAsking] = useState(false);
    // Stays once asked, so the dialog fades out and reads nothing before that.
    const [asked, setAsked] = useState(false);
    const standing = useStanding();
    const { state } = useAuth();
    const member = state.status === "signed-in";
    const { ask } = useGate();
    const { add, adding } = useQueueActions();
    const addSkills = useAddSkills();
    const busy = adding.includes(postingId) || addSkills.isPending;
    // From the press of Try again, not only once the queue answers.
    const again = retrying || (failed && busy);
    const queue = (choices: TailorChoices) =>
        add.mutate(
            { postingId, choices },
            {
                onSuccess: () => setAsking(false),
                // Nothing more to ask here: the upload takes it from this.
                onError: (error) => {
                    if (isNotQueueable(error)) setAsking(false);
                },
            },
        );
    const look = outline ? OUTLINE_BUTTON : GRAD_BUTTON;
    const height = {
        sm: "h-[34px] max-[760px]:min-h-11 text-[12.5px] rounded-[11px]",
        md: "h-10 max-[760px]:min-h-11 text-[13.5px] rounded-[11px]",
        page: "h-10 max-[760px]:min-h-11 text-ui rounded-[11px]",
        tall: "h-[46px] w-full text-[15px] leading-[22px] !font-semibold rounded-control",
        wide: "h-12 w-full text-ui !font-semibold rounded-control",
    }[size];
    if (tailored && resumeId) {
        return (
            <>
                <button
                    {...actionProps("tailored_pdf_open_clicked", {
                        source: "TailorButton",
                        resume_id: resumeId,
                        posting_id: postingId,
                    })}
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        setViewing(true);
                    }}
                    className={`${look} ${height} gap-2 px-4`}
                >
                    <ResumeIcon className="size-4" strokeWidth={2} />
                    See resume
                </button>
                <TailoredResumeDialog
                    open={viewing}
                    row={{ resumeId }}
                    onClose={() => setViewing(false)}
                />
            </>
        );
    }
    return (
        <>
            <button
                {...actionProps("posting_tailor_clicked", {
                    source: "TailorButton",
                    posting_id: postingId,
                    mode: !member ? "visitor" : standing.tailorsOut ? "out" : "member",
                })}
                type="button"
                disabled={queued || tailored || busy || again}
                onClick={(event) => {
                    event.stopPropagation();
                    if (onTailor && !standing.tailorsOut) onTailor();
                    else if (!member) ask("tailor", postingId);
                    else if (standing.tailorsOut) ask("upgrade");
                    else if (failed) add.mutate({ postingId });
                    else {
                        setAsked(true);
                        setAsking(true);
                    }
                }}
                className={`${look} ${height} gap-2 px-4 ${queued && !tailored && !again ? "tailoring-sheen" : ""}`}
            >
                <SparkIcon className="size-4" />
                {tailored
                    ? "Tailored"
                    : again
                      ? "Tailoring…"
                      : queued
                        ? "Tailoring"
                        : busy
                          ? "Adding…"
                          : failed
                            ? "Try again"
                            : "Tailor my resume"}
            </button>
            {asked && (
                <TailorAsk
                    posting={asking ? { posting_id: postingId, ...job } : null}
                    busy={busy}
                    onConfirm={(choices, added) =>
                        // The resume says a skill before a tailor may use it.
                        added.length > 0
                            ? addSkills.mutate(added, { onSuccess: () => queue(choices) })
                            : queue(choices)
                    }
                    onClose={() => setAsking(false)}
                />
            )}
        </>
    );
}
